import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Badge, Button, Card, Input, Spinner } from '@/components/admin/ui';
import {
  EmptyState,
} from '@/components';
import type { StickerPackListItem } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';

function StickerPacksList() {
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'sticker-packs'],
    queryFn: () => apiJson<StickerPackListItem[]>('/v1/admin/sticker-packs'),
  });

  const createPack = useMutation({
    mutationFn: (packName: string) =>
      apiJson<{ id: number }>('/v1/admin/sticker-packs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: packName, is_published: false }),
      }),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-packs'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Sticker Packs</h1>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Create packs and upload sticker images to the CDN.
          </p>
        </div>
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-nova-foreground">New pack</h2>
        <div className="flex flex-wrap gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Pack name"
            className="max-w-xs"
          />
          <Button
            disabled={!name.trim() || createPack.isPending}
            onClick={() => createPack.mutate(name.trim())}
          >
            {createPack.isPending ? 'Creating…' : 'Create pack'}
          </Button>
        </div>
        {createPack.isError ? (
          <p className="text-sm text-red-600">{(createPack.error as Error).message}</p>
        ) : null}
      </Card>

      {!data || data.length === 0 ? (
        <EmptyState title="No sticker packs yet" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((pack) => (
            <Link
              key={pack.id}
              to="/admin/sticker-packs/$id"
              params={{ id: String(pack.id) }}
              className={clsx(
                'group block rounded-lg focus:outline-none',
                'focus-visible:ring-2 focus-visible:ring-nova-ring focus-visible:ring-offset-2'
              )}
            >
              <Card
                className={clsx(
                  'h-full transition',
                  'group-hover:border-nova-primary/20 group-hover:shadow-sm',
                  'group-active:scale-[0.99]'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-medium text-nova-foreground">{pack.name}</h2>
                  <Badge variant={pack.is_published ? 'success' : 'neutral'}>
                    {pack.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                <p className="text-sm text-nova-muted-foreground">{pack.slug}</p>
                <p className="mt-2 text-sm text-nova-muted-foreground">
                  {pack.sticker_count} sticker{pack.sticker_count === 1 ? '' : 's'}
                </p>
                <p className="mt-4 text-sm font-medium text-nova-primary group-hover:underline">
                  Manage pack →
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/sticker-packs')({
  component: function StickerPacksRoute() {
    const matches = useMatches();
    const onDetail = matches.some((m) => m.routeId.includes('sticker-packs/$id'));
    if (onDetail) {
      return <Outlet />;
    }
    return <StickerPacksList />;
  },
});
